import { DataSource } from '@angular/cdk/table';
import { Sort } from '@angular/material/sort';
import { ClientDataroomService } from '@core/dataroom/client-dataroom.service';
import { DataroomService } from '@core/dataroom/dataroom.service';
import { UserDataroomService } from '@core/dataroom/user-dataroom.service';
import { DataSourceOptions } from '@core/models/data-source-options';
import { DataroomNode } from '@core/models/dataroom-node.model';
import { globalSortCompare } from '@core/utils';
import { combineLatest, BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';

export const defaultDataSourceOptions: DataSourceOptions = {
  sort: {
    active: '',
    direction: '',
  },
  search: '',
};

export class DataroomDataSource implements DataSource<DataroomNode> {
  private nodes = new BehaviorSubject<DataroomNode[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(true);
  private options = new BehaviorSubject<DataSourceOptions>(defaultDataSourceOptions);
  private readonly STORAGE_KEY = 'categoryNamesForMandatoryDoc';
  private loadRequestToken = 0;
  private loadSubscription: Subscription | null = null;
  private accessDeniedReasonSubject = new BehaviorSubject<string | null>(null);
  public documents : any;
  public loading = this.loadingSubject.asObservable();
  public accessDeniedReason$ = this.accessDeniedReasonSubject.asObservable();

  constructor(private service: DataroomService | UserDataroomService | ClientDataroomService) { }

  public connect(): Observable<DataroomNode[]> {
    return combineLatest([this.nodes.asObservable(), this.options.asObservable()]).pipe(
      map(([data, options]) => {
        return this.filterData(this.sortData(data, options.sort), options);
      }),
    );
  }

  public disconnect(): void {
    this.loadSubscription?.unsubscribe();
    this.nodes.complete();
    this.loadingSubject.complete();
    this.options.complete();
  }

  public setOptions(options: DataSourceOptions): void {
    this.options.next(options);
  }

  public setFilter(search: string): void {
    this.options.next({
      ...this.options.value,
      search,
    });
  }

  public setCategoryNamesForMandatoryDoc(data: any[]): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
  }

  private getCategoryNamesForMandatoryDoc(): any[] {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (!stored || stored === 'undefined' || stored === 'null') {
      return [];
    }
    try {
      return JSON.parse(stored);
    } catch (error) {
      console.error('Error parsing categoryNamesForMandatoryDoc from localStorage:', error);
      return [];
    }
  }

  public load(sort?: Sort, parentId?: string,documents? : any,vectorStoreJobStatus? : boolean, documentCategoryJobStatus? : boolean, recalculateScoreDocumentId? : string): void {
    
    if(documents) this.documents = documents;
    const requestToken = ++this.loadRequestToken;
    // Cancel any in-flight 'nodes' request before issuing a new one so overlapping
    // load() calls on init don't all hit the network.
    this.loadSubscription?.unsubscribe();
    this.loadingSubject.next(true);
    this.accessDeniedReasonSubject.next(null);
    const categoryNamesForMandatoryDoc = this.getCategoryNamesForMandatoryDoc();

    this.loadSubscription = this.service.getNodes(parentId)
      .pipe(take(1))
      .subscribe({
        next: (nodes) => {
          if (requestToken !== this.loadRequestToken) {
            return;
          }
          const normalizedNodes = this.normalizeNodesResponse(nodes);
          const normalizeNodeStatus = (node: DataroomNode): DataroomNode['status'] => {
            const matchingDocument = this.documents?.find((item: any) => item?.document_id === node?.id);
            const processingStatus = matchingDocument?.is_doc_vectordb_created;
            const documentSupported = node.file?.contentType === 'application/pdf';
            if(!documentSupported){
              return 'notSupported';
            }
            if((!vectorStoreJobStatus || !documentCategoryJobStatus) && processingStatus === 'N' ){
              return 'loading';
            }
            if ( processingStatus === 'N') {
              return 'failed';
            } else if (processingStatus === 'Y') {
              return 'completed';
            } else{
              return 'loading';
            }
          };

          if (categoryNamesForMandatoryDoc?.length === 0) {
            const mappedNodes = normalizedNodes.map((node) =>
              DataroomNode.create({
                ...(node as any),
                name: node?.name || node?.file?.name || 'Untitled',
                status: normalizeNodeStatus(node),
              }),
            );
            this.nodes.next(this.sortData(mappedNodes, sort));
            setTimeout(() => {
              if (requestToken === this.loadRequestToken) {
                this.loadingSubject.next(false);
              }
            }, 0);
            //return;
          }

          const unsupportedDocumentMessage = 'Document is not supported';

          const mapped = normalizedNodes.map(node => {
            const documentSupported = node.file?.contentType === 'application/pdf';
            const isDocumentRecacultingScore = node?.id === recalculateScoreDocumentId;
            let match = null;
            if(documentSupported){
              match = categoryNamesForMandatoryDoc?.find(
              item => String(item.document_id) === String(node.id)
               );
            }
            return DataroomNode.create({
              ...(node as any),
              name: node?.name || node?.file?.name || 'Untitled',
              status: normalizeNodeStatus(node),
              isCalculating: documentSupported ? (isDocumentRecacultingScore ? null : !!(match && match?.category_name)) : !documentSupported,
              category: documentSupported ? (match ? match?.category_name : (node as any)?.category) : unsupportedDocumentMessage,
              aiScore: documentSupported ? (match ? match?.score_level : null) : 'Invalid' 
            } as any);
          });
          this.nodes.next(this.sortData(mapped, sort));
          setTimeout(() => {
            if (requestToken === this.loadRequestToken) {
              this.loadingSubject.next(false);
            }
          }, 0);

        },
        error: (error) => {
          if (requestToken !== this.loadRequestToken) {
            return;
          }
          // The gate on the Issuer's data room answers 403 with a `reason`. A 403 without
          // one comes from the base permission layer (e.g. an investor on the Free tier
          // who was never invited): the backend refuses that user too, so it must show
          // the same "no access" panel and not an empty data room (KAP-1893).
          const reason = error?.error?.reason ?? (error?.status === 403 ? 'forbidden' : null);
          this.accessDeniedReasonSubject.next(reason);
          if (!reason) {
            console.error('Error loading nodes:', error);
          }
          setTimeout(() => {
            if (requestToken === this.loadRequestToken) {
              this.loadingSubject.next(false);
            }
          }, 0);
        }
      });
  }

  private normalizeNodesResponse(payload: unknown): DataroomNode[] {
    const rawNodes = Array.isArray(payload)
      ? payload
      : Array.isArray((payload as any)?.results)
        ? (payload as any).results
        : [];

    return rawNodes.map((raw) => {
      const node: any = { ...(raw as any) };
      if (!node.id && node.uid) {
        node.id = node.uid;
      }
      if (!node.file && node.fileField) {
        node.file = node.fileField;
      }
      if (node.parent && !node.parent.id && node.parent.uid) {
        node.parent.id = node.parent.uid;
      }
      if (Array.isArray(node.ancestors)) {
        node.ancestors = node.ancestors.map((ancestor: any) => ({
          ...ancestor,
          id: ancestor?.id || ancestor?.uid,
        }));
      }
      return DataroomNode.create(node);
    });
  }


  public get data(): DataroomNode[] {
    return this.filterData(this.nodes.getValue(), this.options.getValue());
  }

  public get dataAsObservable(): Observable<DataroomNode[]> {
    return this.nodes.asObservable().pipe(map((data) => this.filterData(data, this.options.getValue())));
  }

  private filterData(data: DataroomNode[], options: DataSourceOptions): DataroomNode[] {
    return data.filter((item) => {
      const nodeName = item?.name || item?.file?.name || '';
      return nodeName.toLowerCase().includes((options.search || '').toLowerCase());
    });
  }

  private sortData(data: DataroomNode[], sort: Sort): DataroomNode[] {
    if (!sort || !sort.direction) {
      sort = defaultDataSourceOptions.sort;
    }

    return data.sort((a, b) => globalSortCompare(a, b, sort));
  }
}
